import type { KeyModifier } from "../types";

export type Platform = "mac" | "windows" | "linux";

export interface KeyDisplay {
  symbol: string;
  name: string;
}

const keyDisplayCache = new Map<string, KeyDisplay>();
const platformHotkeyCache = new Map<string, { key: string; modifiers: KeyModifier[] }>();
const formatModifierCache = new Map<string, string>();
const formatKeyCache = new Map<string, string>();
const formatHotkeyCache = new Map<string, string>();
const platformDetectionCache = { platform: null as Platform | null, timestamp: 0 };

const PLATFORM_CACHE_TTL = 300000;

const PLATFORM_KEY_MAPS: Record<Platform, Record<string, KeyDisplay>> = {
  mac: {
    ctrl: { symbol: "⌃", name: "Control" },
    control: { symbol: "⌃", name: "Control" },
    alt: { symbol: "⌥", name: "Option" },
    option: { symbol: "⌥", name: "Option" },
    shift: { symbol: "⇧", name: "Shift" },
    meta: { symbol: "⌘", name: "Command" },
    cmd: { symbol: "⌘", name: "Command" },
    command: { symbol: "⌘", name: "Command" },
    enter: { symbol: "↩", name: "Return" },
    return: { symbol: "↩", name: "Return" },
    backspace: { symbol: "⌫", name: "Delete" },
    delete: { symbol: "⌦", name: "Forward Delete" },
    escape: { symbol: "⎋", name: "Escape" },
    esc: { symbol: "⎋", name: "Escape" },
    tab: { symbol: "⇥", name: "Tab" },
    space: { symbol: "␣", name: "Space" },
    arrowup: { symbol: "↑", name: "Up" },
    up: { symbol: "↑", name: "Up" },
    arrowdown: { symbol: "↓", name: "Down" },
    down: { symbol: "↓", name: "Down" },
    arrowleft: { symbol: "←", name: "Left" },
    left: { symbol: "←", name: "Left" },
    arrowright: { symbol: "→", name: "Right" },
    right: { symbol: "→", name: "Right" },
    pageup: { symbol: "⇞", name: "Page Up" },
    pagedown: { symbol: "⇟", name: "Page Down" },
    home: { symbol: "↖", name: "Home" },
    end: { symbol: "↘", name: "End" },
  },
  windows: {
    ctrl: { symbol: "Ctrl", name: "Control" },
    control: { symbol: "Ctrl", name: "Control" },
    alt: { symbol: "Alt", name: "Alt" },
    shift: { symbol: "Shift", name: "Shift" },
    meta: { symbol: "Win", name: "Windows" },
    win: { symbol: "Win", name: "Windows" },
    windows: { symbol: "Win", name: "Windows" },
    enter: { symbol: "Enter", name: "Enter" },
    return: { symbol: "Enter", name: "Enter" },
    backspace: { symbol: "Backspace", name: "Backspace" },
    delete: { symbol: "Del", name: "Delete" },
    escape: { symbol: "Esc", name: "Escape" },
    esc: { symbol: "Esc", name: "Escape" },
    tab: { symbol: "Tab", name: "Tab" },
    space: { symbol: "Space", name: "Space" },
    arrowup: { symbol: "↑", name: "Up" },
    up: { symbol: "↑", name: "Up" },
    arrowdown: { symbol: "↓", name: "Down" },
    down: { symbol: "↓", name: "Down" },
    arrowleft: { symbol: "←", name: "Left" },
    left: { symbol: "←", name: "Left" },
    arrowright: { symbol: "→", name: "Right" },
    right: { symbol: "→", name: "Right" },
    pageup: { symbol: "PgUp", name: "Page Up" },
    pagedown: { symbol: "PgDn", name: "Page Down" },
    home: { symbol: "Home", name: "Home" },
    end: { symbol: "End", name: "End" },
  },
  linux: {
    ctrl: { symbol: "Ctrl", name: "Control" },
    control: { symbol: "Ctrl", name: "Control" },
    alt: { symbol: "Alt", name: "Alt" },
    shift: { symbol: "Shift", name: "Shift" },
    meta: { symbol: "Super", name: "Super" },
    super: { symbol: "Super", name: "Super" },
    enter: { symbol: "Enter", name: "Enter" },
    return: { symbol: "Enter", name: "Enter" },
    backspace: { symbol: "Backspace", name: "Backspace" },
    delete: { symbol: "Del", name: "Delete" },
    escape: { symbol: "Esc", name: "Escape" },
    esc: { symbol: "Esc", name: "Escape" },
    tab: { symbol: "Tab", name: "Tab" },
    space: { symbol: "Space", name: "Space" },
    arrowup: { symbol: "↑", name: "Up" },
    up: { symbol: "↑", name: "Up" },
    arrowdown: { symbol: "↓", name: "Down" },
    down: { symbol: "↓", name: "Down" },
    arrowleft: { symbol: "←", name: "Left" },
    left: { symbol: "←", name: "Left" },
    arrowright: { symbol: "→", name: "Right" },
    right: { symbol: "→", name: "Right" },
    pageup: { symbol: "PgUp", name: "Page Up" },
    pagedown: { symbol: "PgDn", name: "Page Down" },
    home: { symbol: "Home", name: "Home" },
    end: { symbol: "End", name: "End" },
  },
};

export function detectPlatform(): Platform {
  const now = Date.now();
  if (platformDetectionCache.platform && now - platformDetectionCache.timestamp < PLATFORM_CACHE_TTL) {
    return platformDetectionCache.platform;
  }

  let platform: Platform = "windows";

  if (typeof navigator !== "undefined") {
    const platformLower = navigator.platform.toLowerCase();
    const userAgentLower = navigator.userAgent.toLowerCase();

    if (platformLower.includes("mac") || userAgentLower.includes("mac")) {
      platform = "mac";
    } else if (platformLower.includes("linux") || userAgentLower.includes("linux")) {
      platform = "linux";
    }
  }

  platformDetectionCache.platform = platform;
  platformDetectionCache.timestamp = now;

  return platform;
}

let cachedPlatform: Platform | null = null;
export function getPlatform(): Platform {
  if (cachedPlatform === null) {
    cachedPlatform = detectPlatform();
  }
  return cachedPlatform;
}

export function getKeyDisplay(key: string, platform?: Platform): KeyDisplay {
  const targetPlatform = platform || getPlatform();
  const cacheKey = `${key.toLowerCase()}:${targetPlatform}`;

  const cached = keyDisplayCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const normalizedKey = key.toLowerCase();
  const map = PLATFORM_KEY_MAPS[targetPlatform];

  let result: KeyDisplay;

  if (map[normalizedKey]) {
    result = map[normalizedKey];
  } else {
    const displayName = key.length === 1 ? key.toUpperCase() : key.charAt(0).toUpperCase() + key.slice(1);
    result = {
      symbol: displayName,
      name: displayName,
    };
  }

  keyDisplayCache.set(cacheKey, result);

  return result;
}

export function formatModifier(modifier: KeyModifier, options: { useSymbol?: boolean; platform?: Platform } = {}): string {
  const { useSymbol = true, platform } = options;
  const targetPlatform = platform || getPlatform();
  const cacheKey = `${modifier}:${useSymbol}:${targetPlatform}`;

  const cached = formatModifierCache.get(cacheKey);
  if (cached !== undefined) {
    return cached;
  }

  const display = getKeyDisplay(modifier, targetPlatform);
  const result = useSymbol ? display.symbol : display.name;

  formatModifierCache.set(cacheKey, result);

  return result;
}

export function formatKey(key: string, options: { useSymbol?: boolean; platform?: Platform } = {}): string {
  const { useSymbol = true, platform } = options;
  const targetPlatform = platform || getPlatform();
  const cacheKey = `${key}:${useSymbol}:${targetPlatform}`;

  const cached = formatKeyCache.get(cacheKey);
  if (cached !== undefined) {
    return cached;
  }

  const display = getKeyDisplay(key, targetPlatform);
  const result = useSymbol ? display.symbol : display.name;

  formatKeyCache.set(cacheKey, result);

  return result;
}

export function formatHotkey(
  key: string,
  modifiers?: KeyModifier[],
  options: { useSymbol?: boolean; separator?: string; platform?: Platform } = {},
): string {
  const { useSymbol = true, separator = "+", platform } = options;
  const targetPlatform = platform || getPlatform();
  const sortedModifiers = (modifiers || []).sort();
  const cacheKey = `${key}:${sortedModifiers.join(",")}:${useSymbol}:${separator}:${targetPlatform}`;

  const cached = formatHotkeyCache.get(cacheKey);
  if (cached !== undefined) {
    return cached;
  }

  const parts: string[] = [];

  const modifierOrder: KeyModifier[] = ["ctrl", "alt", "shift", "meta"];

  if (modifiers && modifiers.length > 0) {
    modifierOrder.forEach((mod) => {
      if (modifiers.includes(mod)) {
        parts.push(formatModifier(mod, { useSymbol, platform: targetPlatform }));
      }
    });
  }

  parts.push(formatKey(key, { useSymbol, platform: targetPlatform }));

  const result = parts.join(separator);

  formatHotkeyCache.set(cacheKey, result);

  return result;
}

export function convertToMac(key: string, modifiers?: KeyModifier[]): { key: string; modifiers: KeyModifier[] } {
  const newModifiers = [...(modifiers || [])];

  const ctrlIndex = newModifiers.indexOf("ctrl");
  if (ctrlIndex !== -1) {
    newModifiers[ctrlIndex] = "meta";
  }

  return { key, modifiers: newModifiers };
}

export function convertToWindows(key: string, modifiers?: KeyModifier[]): { key: string; modifiers: KeyModifier[] } {
  const newModifiers = [...(modifiers || [])];

  const metaIndex = newModifiers.indexOf("meta");
  if (metaIndex !== -1) {
    newModifiers[metaIndex] = "ctrl";
  }

  return { key, modifiers: newModifiers };
}

export function getPlatformHotkey(
  key: string,
  modifiers?: KeyModifier[],
  targetPlatform?: Platform,
): { key: string; modifiers: KeyModifier[] } {
  const platform = targetPlatform || getPlatform();
  const sortedModifiers = (modifiers || []).sort();
  const cacheKey = `${key}:${sortedModifiers.join(",")}:${platform}`;

  const cached = platformHotkeyCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  let result: { key: string; modifiers: KeyModifier[] };

  if (platform === "mac") {
    result = convertToMac(key, modifiers);
  } else {
    result = convertToWindows(key, modifiers);
  }

  platformHotkeyCache.set(cacheKey, result);

  return result;
}

export function isPrimaryModifier(modifier: KeyModifier, platform?: Platform): boolean {
  const targetPlatform = platform || getPlatform();

  if (targetPlatform === "mac") {
    return modifier === "meta";
  }

  return modifier === "ctrl";
}

export function getPrimaryModifier(platform?: Platform): KeyModifier {
  const targetPlatform = platform || getPlatform();
  return targetPlatform === "mac" ? "meta" : "ctrl";
}

export const platformKeysPerformance = {
  clearCache(): void {
    keyDisplayCache.clear();
    platformHotkeyCache.clear();
    formatModifierCache.clear();
    formatKeyCache.clear();
    formatHotkeyCache.clear();
    platformDetectionCache.platform = null;
    platformDetectionCache.timestamp = 0;
    cachedPlatform = null;
  },

  getCacheStats(): {
    keyDisplay: number;
    platformHotkey: number;
    formatModifier: number;
    formatKey: number;
    formatHotkey: number;
    platformDetection: boolean;
    totalMemoryEstimate: string;
  } {
    const estimate =
      keyDisplayCache.size * 60
      + platformHotkeyCache.size * 80
      + formatModifierCache.size * 20
      + formatKeyCache.size * 20
      + formatHotkeyCache.size * 40
      + (platformDetectionCache.platform ? 50 : 0);

    return {
      keyDisplay: keyDisplayCache.size,
      platformHotkey: platformHotkeyCache.size,
      formatModifier: formatModifierCache.size,
      formatKey: formatKeyCache.size,
      formatHotkey: formatHotkeyCache.size,
      platformDetection: platformDetectionCache.platform !== null,
      totalMemoryEstimate: `~${(estimate / 1024).toFixed(1)}KB`,
    };
  },

  preloadCommonKeys(): void {
    const currentPlatform = getPlatform();

    const modifiers: KeyModifier[] = ["ctrl", "alt", "shift", "meta"];
    modifiers.forEach((mod) => {
      getKeyDisplay(mod, currentPlatform);
      formatModifier(mod, { useSymbol: true, platform: currentPlatform });
      formatModifier(mod, { useSymbol: false, platform: currentPlatform });
    });

    const commonKeys = [
      "enter",
      "escape",
      "space",
      "tab",
      "backspace",
      "delete",
      "arrowup",
      "arrowdown",
      "arrowleft",
      "arrowright",
      "home",
      "end",
      "pageup",
      "pagedown",
      "a",
      "s",
      "c",
      "v",
      "x",
      "z",
      "y",
      "n",
      "o",
      "p",
      "q",
      "w",
      "f",
    ];

    commonKeys.forEach((key) => {
      getKeyDisplay(key, currentPlatform);
      formatKey(key, { useSymbol: true, platform: currentPlatform });
      formatKey(key, { useSymbol: false, platform: currentPlatform });
    });

    const commonHotkeys = [
      { key: "c", modifiers: ["ctrl"] as KeyModifier[] },
      { key: "v", modifiers: ["ctrl"] as KeyModifier[] },
      { key: "x", modifiers: ["ctrl"] as KeyModifier[] },
      { key: "z", modifiers: ["ctrl"] as KeyModifier[] },
      { key: "y", modifiers: ["ctrl"] as KeyModifier[] },
      { key: "s", modifiers: ["ctrl"] as KeyModifier[] },
      { key: "n", modifiers: ["ctrl"] as KeyModifier[] },
      { key: "o", modifiers: ["ctrl"] as KeyModifier[] },
      { key: "p", modifiers: ["ctrl"] as KeyModifier[] },
      { key: "a", modifiers: ["ctrl"] as KeyModifier[] },
      { key: "f", modifiers: ["ctrl"] as KeyModifier[] },
    ];

    commonHotkeys.forEach(({ key, modifiers }) => {
      formatHotkey(key, modifiers, { useSymbol: true, platform: currentPlatform });
      formatHotkey(key, modifiers, { useSymbol: false, platform: currentPlatform });
      getPlatformHotkey(key, modifiers, currentPlatform);
    });
  },

  invalidatePlatformDetection(): void {
    platformDetectionCache.platform = null;
    platformDetectionCache.timestamp = 0;
    cachedPlatform = null;

    keyDisplayCache.clear();
    platformHotkeyCache.clear();
    formatModifierCache.clear();
    formatKeyCache.clear();
    formatHotkeyCache.clear();
  },
};
